import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { positionAPI, employeeAPI, departmentAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

function slugify(text) {
  return (text || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[đĐ]/g, m => m === 'đ' ? 'd' : 'D')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

const LEVELS = {
  junior:   { label: 'Nhân viên',           color: '#64748b', bg: '#f1f5f9' },
  mid:      { label: 'Chuyên viên',          color: '#0891b2', bg: '#e0f2fe' },
  senior:   { label: 'Chuyên viên cao cấp',  color: '#7c3aed', bg: '#ede9fe' },
  lead:     { label: 'Trưởng nhóm',          color: '#0f766e', bg: '#ccfbf1' },
  manager:  { label: 'Quản lý',              color: '#b45309', bg: '#fef9c3' },
  director: { label: 'Giám đốc',             color: '#1e3a8a', bg: '#dbeafe' },
  c_level:  { label: 'Ban lãnh đạo',         color: '#9f1239', bg: '#ffe4e6' },
};

const CONTRACT_LABEL = {
  permanent: { label: 'Chính thức',    color: '#16a34a', bg: '#dcfce7' },
  probation: { label: 'Thử việc',      color: '#d97706', bg: '#fef3c7' },
  fixed:     { label: 'Có thời hạn',   color: '#0891b2', bg: '#cffafe' },
  parttime:  { label: 'Bán thời gian', color: '#7c3aed', bg: '#ede9fe' },
};

const fmt = (n) => n ? Number(n).toLocaleString('vi-VN') : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '—';

function Avatar({ name, size = 34 }) {
  const initials = (name || '?').split(' ').map(w => w[0]).slice(-2).join('').toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg,#1e3a8a,#0891b2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size * 0.36,
    }}>{initials}</div>
  );
}

export default function PositionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ['admin', 'hr_manager'].includes(user?.role);

  const [position, setPosition] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Edit state
  const [editModal, setEditModal] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      positionAPI.list(),
      employeeAPI.list(),
      departmentAPI.list(),
    ])
      .then(([pRes, eRes, dRes]) => {
        const pos = (pRes.data.data || []).find(p => String(p.id) === String(id));
        if (!pos) { setError('Không tìm thấy chức danh'); return; }
        setPosition(pos);
        setEmployees((eRes.data.data || []).filter(e => String(e.position_id) === String(id)));
        setDepartments(dRes.data.data || []);
      })
      .catch(() => setError('Không thể tải dữ liệu'))
      .finally(() => setLoading(false));
  }, [id]);

  const openEdit = () => {
    setForm({
      title: position.title,
      level: position.level || 'mid',
      department_id: position.department_id || '',
      salary_min: position.salary_min || '',
      salary_max: position.salary_max || '',
    });
    setFormError('');
    setEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.salary_min && form.salary_max && Number(form.salary_min) > Number(form.salary_max)) {
      setFormError('Lương tối thiểu không được lớn hơn lương tối đa');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      await positionAPI.update(id, {
        title: form.title,
        level: form.level,
        department_id: form.department_id || null,
        salary_min: form.salary_min || 0,
        salary_max: form.salary_max || 0,
      });
      const res = await positionAPI.list();
      const updated = (res.data.data || []).find(p => String(p.id) === String(id));
      setPosition(updated);
      setEditModal(false);
      navigate(`/positions/${id}/${slugify(updated.title)}`, { replace: true });
    } catch (err) {
      setFormError(err.response?.data?.message || 'Lỗi lưu chức danh');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748b' }}>
      Đang tải...
    </div>
  );

  if (error || !position) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>😞</div>
      <div style={{ fontSize: 15, color: '#64748b', marginBottom: 20 }}>{error || 'Không tìm thấy chức danh'}</div>
      <button onClick={() => navigate('/positions')} style={{
        padding: '8px 20px', borderRadius: 8, border: 'none',
        background: 'linear-gradient(135deg,#1e3a8a,#0891b2)', color: '#fff', cursor: 'pointer', fontWeight: 600,
      }}>← Quay lại</button>
    </div>
  );

  const lv = LEVELS[position.level] || LEVELS.mid;
  const activeEmps = employees.filter(e => e.status === 'active');
  const filteredEmps = employees.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(s) ||
      (e.employee_code || '').toLowerCase().includes(s) ||
      (e.department_name || '').toLowerCase().includes(s)
    );
  });

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13, color: '#64748b' }}>
        <button onClick={() => navigate('/positions')} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#1d4ed8', fontWeight: 600, fontSize: 13, padding: 0,
        }}>← Chức danh</button>
        {position.department_name && (
          <>
            <span>/</span>
            <span>{position.department_name}</span>
          </>
        )}
        <span>/</span>
        <span style={{ color: '#1e293b', fontWeight: 500 }}>{position.title}</span>
      </div>

      {/* Header card */}
      <div style={{
        background: '#fff', borderRadius: 14,
        boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
        overflow: 'hidden', marginBottom: 20,
      }}>
        <div style={{ height: 5, background: `linear-gradient(90deg,${lv.color},${lv.color}66)` }} />
        <div style={{ padding: '24px 28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{
                width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                background: lv.bg, border: `2px solid ${lv.color}30`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
              }}>💼</div>
              <div>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: 0 }}>{position.title}</h1>
                <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{
                    fontSize: 12, fontWeight: 700, padding: '3px 12px', borderRadius: 20,
                    color: lv.color, background: lv.bg,
                  }}>{lv.label}</span>
                  {position.department_name && (
                    <span style={{ fontSize: 12, color: '#64748b', background: '#f1f5f9', padding: '3px 10px', borderRadius: 10 }}>
                      🏬 {position.department_name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {canEdit && (
              <button onClick={openEdit} style={{
                padding: '7px 18px', borderRadius: 8, border: '1px solid #e2e8f0',
                background: '#fff', cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 600,
              }}>✏️ Chỉnh sửa</button>
            )}
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 16, marginTop: 20, flexWrap: 'wrap' }}>
            {[
              { icon: '👥', label: 'Đang giữ chức danh', value: activeEmps.length, color: '#1e3a8a' },
              { icon: '📋', label: 'Tổng nhân viên',     value: employees.length,  color: '#0891b2' },
              {
                icon: '💰', color: '#16a34a',
                label: 'Lương tối thiểu',
                value: position.salary_min > 0 ? `${fmt(position.salary_min)} ₫` : 'Chưa thiết lập',
              },
              {
                icon: '💎', color: '#7c3aed',
                label: 'Lương tối đa',
                value: position.salary_max > 0 ? `${fmt(position.salary_max)} ₫` : 'Chưa thiết lập',
              },
            ].map(s => (
              <div key={s.label} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: '#f8fafc', borderRadius: 10, padding: '10px 16px', flex: '1 1 160px',
              }}>
                <span style={{ fontSize: 20 }}>{s.icon}</span>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Employees table */}
      <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>👥 Nhân viên đang giữ chức danh</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Tìm theo tên, mã NV, phòng ban..."
            style={{ flex: 1, padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
          />
          <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
            {filteredEmps.length}/{employees.length} nhân viên
          </span>
        </div>

        {filteredEmps.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>💼</div>
            <div>{search ? 'Không tìm thấy nhân viên phù hợp' : 'Chưa có nhân viên nào giữ chức danh này'}</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Nhân viên', 'Mã NV', 'Phòng ban', 'Loại HĐ', 'Ngày vào', 'Trạng thái'].map(h => (
                  <th key={h} style={{
                    padding: '11px 16px', textAlign: 'left', fontSize: 11,
                    fontWeight: 700, color: '#374151', textTransform: 'uppercase',
                    letterSpacing: '0.4px', borderBottom: '2px solid #e2e8f0',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredEmps.map(emp => {
                const ct = CONTRACT_LABEL[emp.contract_type] || CONTRACT_LABEL.permanent;
                const isActive = emp.status === 'active';
                return (
                  <tr key={emp.id}
                    onClick={() => navigate(`/employees/${emp.id}`)}
                    style={{ borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '11px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={`${emp.first_name} ${emp.last_name}`} />
                        <div>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>{emp.last_name} {emp.first_name}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{emp.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#0891b2', background: '#f0f9ff', padding: '2px 8px', borderRadius: 4 }}>
                        {emp.employee_code}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px', color: '#64748b' }}>
                      {emp.department_name
                        ? <span style={{ background: '#f1f5f9', padding: '3px 9px', borderRadius: 6, fontSize: 12 }}>{emp.department_name}</span>
                        : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10, color: ct.color, background: ct.bg }}>
                        {ct.label}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px', color: '#64748b' }}>{fmtDate(emp.join_date)}</td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                        color: isActive ? '#16a34a' : '#dc2626',
                        background: isActive ? '#dcfce7' : '#fee2e2',
                      }}>
                        {isActive ? 'Đang làm' : emp.status === 'resigned' ? 'Đã nghỉ' : 'Không hoạt động'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }} onClick={e => { if (e.target === e.currentTarget) setEditModal(false); }}>
          <div style={{
            background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520,
            maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{
              padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              position: 'sticky', top: 0, background: '#fff', zIndex: 1,
            }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>✏️ Chỉnh sửa chức danh</div>
              <button onClick={() => setEditModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8' }}>×</button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                  Tên chức danh <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                    Cấp độ <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select value={form.level} onChange={e => setForm(f => ({ ...f, level: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
                    {Object.entries(LEVELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                  {form.level && (
                    <div style={{ marginTop: 6 }}>
                      <span style={{ background: LEVELS[form.level]?.bg, color: LEVELS[form.level]?.color, padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                        {LEVELS[form.level]?.label}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Phòng ban</label>
                  <select value={form.department_id} onChange={e => setForm(f => ({ ...f, department_id: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
                    <option value="">— Không gắn phòng ban —</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Khoảng lương (₫/tháng)</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <input type="number" min="0" placeholder="Tối thiểu" value={form.salary_min}
                      onChange={e => setForm(f => ({ ...f, salary_min: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                    {form.salary_min > 0 && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{fmt(form.salary_min)} ₫</div>}
                  </div>
                  <div>
                    <input type="number" min="0" placeholder="Tối đa" value={form.salary_max}
                      onChange={e => setForm(f => ({ ...f, salary_max: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                    {form.salary_max > 0 && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{fmt(form.salary_max)} ₫</div>}
                  </div>
                </div>
              </div>

              {formError && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>⚠️ {formError}</div>}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button type="button" onClick={() => setEditModal(false)}
                  style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}>
                  Huỷ
                </button>
                <button type="submit" disabled={submitting} style={{
                  padding: '9px 24px', borderRadius: 8, border: 'none',
                  background: submitting ? '#93c5fd' : 'linear-gradient(135deg,#1e3a8a,#0891b2)',
                  color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14,
                }}>{submitting ? '⏳ Đang lưu...' : 'Cập nhật'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
